/**
 * Member App with Code Splitting
 * Phase 5.1.1: React.lazy for performance optimization
 * Phase 5.5.1: Error Boundary integration
 */

import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { isAuthenticated } from './lib/auth';
import { Loading } from './components/Loading';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load pages for code splitting
const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const MeetingReview = lazy(() =>
  import('./pages/MeetingReview').then((m) => ({ default: m.MeetingReview }))
);
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const AuthCallback = lazy(() =>
  import('./pages/AuthCallback').then((m) => ({ default: m.AuthCallback }))
);

/**
 * Loading fallback component for Suspense
 */
function PageLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loading />
    </div>
  );
}

/**
 * Auth guard - redirects to /login if not authenticated
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  if (!isAuthenticated()) {
    return null;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <LandingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/meetings/:id/review"
            element={
              <RequireAuth>
                <MeetingReview />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
